import { useRef, useState } from "react"
import type { NodeViewProps } from "@tiptap/react"
import { NodeViewWrapper } from "@tiptap/react"
import { Button } from "../../tiptap-ui-primitive/button"
import { CloseIcon } from "../../tiptap-icons/close-icon"
import "./file-upload-node.scss"
import { isValidPosition } from "../../lib/tiptap-utils"

interface FileItem {
  id: string
  file: File
  progress: number
  status: "uploading" | "success" | "error"
  url?: string
  abortController?: AbortController
}

interface FileUploadOptions {
  maxSize: number
  limit: number
  accept: string
  upload: (file: File, onProgress: (event: { progress: number }) => void, signal: AbortSignal) => Promise<string>
  onSuccess?: (url: string, fileName: string) => void
  onError?: (error: Error) => void
}

function useFileUpload(options: FileUploadOptions) {
  const [fileItems, setFileItems] = useState<FileItem[]>([])

  const uploadFile = async (file: File): Promise<{ url: string; fileName: string } | null> => {
    if (options.maxSize && file.size > options.maxSize) {
      options.onError?.(new Error(`Размер файла превышает ${options.maxSize / 1024 / 1024}МБ`))
      return null
    }

    const abortController = new AbortController()
    const fileId = crypto.randomUUID()

    setFileItems((prev) => [...prev, {
      id: fileId,
      file,
      progress: 0,
      status: "uploading",
      abortController,
    }])

    try {
      if (!options.upload) {
        throw new Error("Функция загрузки не определена")
      }

      const url = await options.upload(
        file,
        (event) => {
          setFileItems((prev) =>
            prev.map((item) =>
              item.id === fileId ? { ...item, progress: event.progress } : item
            )
          )
        },
        abortController.signal
      )

      if (!url) throw new Error("Ошибка загрузки: URL не получен")

      if (!abortController.signal.aborted) {
        setFileItems((prev) =>
          prev.map((item) =>
            item.id === fileId ? { ...item, status: "success", url, progress: 100 } : item
          )
        )
        options.onSuccess?.(url, file.name)
        return { url, fileName: file.name }
      }

      return null
    } catch (error) {
      if (!abortController.signal.aborted) {
        setFileItems((prev) =>
          prev.map((item) =>
            item.id === fileId ? { ...item, status: "error", progress: 0 } : item
          )
        )
        options.onError?.(error instanceof Error ? error : new Error("Ошибка загрузки"))
      }
      return null
    }
  }

  const uploadFiles = async (files: File[]): Promise<{ url: string; fileName: string }[]> => {
    if (!files || files.length === 0) {
      options.onError?.(new Error("Нет файлов для загрузки"))
      return []
    }

    if (options.limit && files.length > options.limit) {
      options.onError?.(new Error(`Максимум ${options.limit} файлов`))
      return []
    }

    const results = await Promise.all(files.map((file) => uploadFile(file)))
    return results.filter((r): r is { url: string; fileName: string } => r !== null)
  }

  const removeFileItem = (fileId: string) => {
    setFileItems((prev) => {
      const item = prev.find((i) => i.id === fileId)
      item?.abortController?.abort()
      return prev.filter((i) => i.id !== fileId)
    })
  }

  return { fileItems, uploadFiles, removeFileItem }
}

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return "0 Bytes"
  const k = 1024
  const sizes = ["Bytes", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

const FileUploadIcon: React.FC = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="tiptap-file-upload-icon">
    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 18V12M9 15L12 12L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

export const FileUploadNode: React.FC<NodeViewProps> = (props) => {
  const { accept, limit, maxSize } = props.node.attrs
  const inputRef = useRef<HTMLInputElement>(null)
  const extension = props.extension

  const uploadOptions: FileUploadOptions = {
    maxSize: maxSize || 10 * 1024 * 1024,
    limit: limit || 1,
    accept: accept || "*",
    upload: extension.options.upload,
    onSuccess: extension.options.onSuccess,
    onError: extension.options.onError,
  }

  const { fileItems, uploadFiles, removeFileItem } = useFileUpload(uploadOptions)

  const handleUpload = async (files: File[]) => {
    const results = await uploadFiles(files)

    if (results.length > 0) {
      const pos = props.getPos()

      if (isValidPosition(pos)) {
        // Создаем массив узлов fileNode для вставки
        const fileNodes = results.map(({ url, fileName }) => {
          const originalFile = files.find(f => f.name === fileName)
          return {
            type: "fileNode",
            attrs: {
              src: url,
              fileName: fileName,
              fileSize: originalFile?.size || null,
            },
          }
        })

        props.editor
          .chain()
          .focus()
          .deleteRange({ from: pos, to: pos + props.node.nodeSize })
          .insertContentAt(pos, fileNodes)
          .run()
      }
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    handleUpload(Array.from(files))
  }

  const handleClick = () => {
    if (inputRef.current && fileItems.length === 0) {
      inputRef.current.value = ""
      inputRef.current.click()
    }
  }

  const handleRemoveNode = (e: React.MouseEvent) => {
    e.stopPropagation()
    const pos = props.getPos()
    if (isValidPosition(pos)) {
      props.editor
        .chain()
        .focus()
        .deleteRange({ from: pos, to: pos + props.node.nodeSize })
        .run()
    }
  }

  return (
    <NodeViewWrapper className="tiptap-file-upload" onClick={handleClick}>
      {fileItems.length === 0 && (
        <div className="tiptap-file-upload-dropzone">
          <Button 
            className="tiptap-upload-close-btn" 
            data-style="ghost" 
            onClick={handleRemoveNode}
            title="Отменить"
          >
            <CloseIcon className="tiptap-button-icon" />
          </Button>
          <FileUploadIcon />
          <div className="tiptap-file-upload-text">
            <span>Нажмите для загрузки</span> или перетащите файл
          </div>
          <div className="tiptap-file-upload-subtext">
            Макс. {uploadOptions.maxSize / 1024 / 1024}МБ
          </div>
        </div>
      )}

      {fileItems.length > 0 && (
        <div className="tiptap-file-upload-previews">
          {fileItems.map((item) => (
            <div key={item.id} className="tiptap-file-upload-preview">
              {item.status === "uploading" && (
                <div className="tiptap-file-upload-progress" style={{ width: `${item.progress}%` }} />
              )}
              <span>{item.file.name}</span>
              <Button data-style="ghost" onClick={(e) => { e.stopPropagation(); removeFileItem(item.id) }}>
                <CloseIcon className="tiptap-button-icon" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={limit > 1}
        onChange={handleChange}
        onClick={(e) => e.stopPropagation()}
      />
    </NodeViewWrapper>
  )
}
